import _ from 'lodash';
import { UsersAPI, SensorsAPI, LocationsAPI } from '../../lib/api';
import { auth } from '../../lib/auth';
import { normalizeError } from '../../lib/helpers';

// We need page-level actions so we can refresh pages when things
// change in the detail view, e.g.
import * as Page from './Page';

export const DETAIL_PRESENT = 'DETAIL_PRESENT';
export const DETAIL_DISMISS = 'DETAIL_DISMISS';
export const DETAIL_ERROR = 'DETAIL_ERROR';
export const DETAIL_DATA_UPDATE_START = 'DETAIL_DATA_UPDATE_START';
export const DETAIL_DATA_UPDATE_SUCCESS = 'DETAIL_DATA_UPDATE_SUCCESS';
export const DETAIL_DATA_UPDATE_FAILURE = 'DETAIL_DATA_UPDATE_FAILURE';
export const DETAIL_DATA_FETCH_START = 'DETAIL_DATA_FETCH_START';
export const DETAIL_DATA_FETCH_SUCCESS = 'DETAIL_DATA_FETCH_SUCCESS';
export const DETAIL_DATA_FETCH_FAILURE = 'DETAIL_DATA_FETCH_FAILURE';

export const DATA_TYPE_SENSOR = 'sensor';
export const DATA_TYPE_SENSOR_MODULE = 'sensor.module';

const API = {
  users: new UsersAPI(),
  sensors: new SensorsAPI(),
  locations: new LocationsAPI(),
};

function extractStackInfo(stackIdOrIndex) {
  const stackInfo = {};
  if (_.isNumber(stackIdOrIndex)) {
    stackInfo.stackIndex = stackIdOrIndex;
  } else {
    stackInfo.stackId = stackIdOrIndex;
  }
  return stackInfo;
}

function present(dataType, stackIdOrIndex, options) {
  return {
    type: DETAIL_PRESENT,
    dataType,
    ...options,
    ...extractStackInfo(stackIdOrIndex),
  };
}

function internalDismiss(stackIdOrIndex) {
  return {
    type: DETAIL_DISMISS,
    ...extractStackInfo(stackIdOrIndex),
  };
}

function dataFetchStart(dataType, stackIdOrIndex) {
  return {
    type: DETAIL_DATA_FETCH_START,
    dataType,
    ...extractStackInfo(stackIdOrIndex),
  };
}

function dataFetchSuccess(params, data, dataType, stackIdOrIndex) {
  return {
    type: DETAIL_DATA_FETCH_SUCCESS,
    params,
    data,
    dataType,
    ...extractStackInfo(stackIdOrIndex),
  };
}

function dataFetchFailure(params, error, dataType, stackIdOrIndex) {
  return {
    type: DETAIL_DATA_FETCH_FAILURE,
    params,
    error,
    dataType,
    ...extractStackInfo(stackIdOrIndex),
  };
}

function dataUpdateStart(dataType, stackIdOrIndex) {
  return {
    type: DETAIL_DATA_UPDATE_START,
    dataType,
    ...extractStackInfo(stackIdOrIndex),
  };
}

function dataUpdateSuccess(params, data, dataType, stackIdOrIndex) {
  return {
    type: DETAIL_DATA_UPDATE_SUCCESS,
    params,
    data,
    dataType,
    ...extractStackInfo(stackIdOrIndex),
  };
}

function dataUpdateFailure(params, error, dataType, stackIdOrIndex) {
  const normalizedError = normalizeError(error);

  return {
    type: DETAIL_DATA_UPDATE_FAILURE,
    params,
    error: normalizedError,
    dataType,
    ...extractStackInfo(stackIdOrIndex),
  };
}

//---------------------------------------------------------------------------
// Re-usable thunks / mixins
//---------------------------------------------------------------------------
const fetchSensorDetailMixin = (orgId, personId, sensorId, stackIdOrIndex) =>
  (dispatch) => {
    dispatch(dataFetchStart(DATA_TYPE_SENSOR, stackIdOrIndex));

    const compositeData = {};
    const errors = [];

    const promises = [
      // Need locations
      API.locations.list(orgId).then(
        (data) => { compositeData.locations = data.data; },
        (error) => { errors.push(error); }
      ),
    ];

    if (sensorId) {
      // and GET /sensor/{id}
      promises.push(API.sensors.get(orgId, personId, sensorId).then(
        (data) => { compositeData.sensor = data.data; },
        (error) => { errors.push(error); }
      ));
      // And of course this means we're in 'edit' mode in the detail view
      compositeData.mode = 'edit';
    } else {
      // this is create mode
      compositeData.mode = 'create';
      compositeData.sensor = {};
    }

    Promise.all(promises).then(() => {
      // Just the first error
      const error = _.first(errors);
      if (error) {
        dispatch(dataFetchFailure(
          { orgId, personId, sensorId },
          normalizeError(error, { prependMessage: 'This sensor could not be retrieved:' }),
          DATA_TYPE_SENSOR,
          stackIdOrIndex
        ));
      } else {
        dispatch(dataFetchSuccess(
          { orgId, personId, sensorId },
          compositeData,
          DATA_TYPE_SENSOR,
          stackIdOrIndex
        ));
      }
    });
  };

//---------------------------------------------------------------------------
// Exported action creators.
//---------------------------------------------------------------------------
export const presentSensorModuleEdit = (sensor, module, stackIdOrIndex = 'primary-sensor-module', parentStackId = null) =>
  (dispatch) => {
    dispatch(present(DATA_TYPE_SENSOR_MODULE, stackIdOrIndex, { parentStackId }));
    dispatch(dataFetchSuccess({ module }, { sensor, module }, DATA_TYPE_SENSOR_MODULE, stackIdOrIndex));
  };

export const presentSensorModuleCreate = (sensor, stackIdOrIndex = 'primary-sensor-module', parentStackId = null) =>
  presentSensorModuleEdit(sensor, null, stackIdOrIndex, parentStackId);

export const presentSensorEdit = (orgId, sensorId, stackIdOrIndex = 'primary-sensor') => {
  const personId = auth.person_id;

  return (dispatch) => {
    dispatch(present(DATA_TYPE_SENSOR, stackIdOrIndex, { clearStack: true }));
    dispatch(fetchSensorDetailMixin(orgId, personId, sensorId, stackIdOrIndex));
  };
};

export const presentSensorCreate = (orgId, stackIdOrIndex = 'primary-sensor') =>
  presentSensorEdit(orgId, null, stackIdOrIndex);

export const dismiss = stackIdOrIndex => internalDismiss(stackIdOrIndex);

export const reportError = (error, stackIdOrIndex) => ({
  type: DETAIL_ERROR,
  error: normalizeError(error),
  ...extractStackInfo(stackIdOrIndex),
});

export const createSensor = (orgId, sensorData, stackIdOrIndex) => {
  const personId = auth.person_id;

  return (dispatch) => {
    dispatch(dataUpdateStart(DATA_TYPE_SENSOR, stackIdOrIndex));

    API.sensors.create(orgId, personId, sensorData).then(
      (data) => {
        const newSensorId = data.data.id;

        // Tell the detail view about our great success
        dispatch(dataUpdateSuccess(
          { orgId, sensorData, newSensorId },
          data,
          DATA_TYPE_SENSOR,
          stackIdOrIndex
        ));
        // Re-fetch the sensor for the detail view
        dispatch(fetchSensorDetailMixin(orgId, personId, newSensorId, stackIdOrIndex));
        // Re-fetch sensors if pages are displaying them
        dispatch(Page.fetchSensors(orgId));
      },
      (error) => {
        dispatch(dataUpdateFailure(
          { orgId, sensorData },
          normalizeError(error, { prependMessage: 'This sensor could not be created:' }),
          DATA_TYPE_SENSOR,
          stackIdOrIndex
        ));
      }
    );
  };
};

export const updateSensor = (orgId, sensorData, stackIdOrIndex) => {
  const personId = auth.person_id;

  return (dispatch) => {
    const sensorId = sensorData.id;
    dispatch(dataUpdateStart(DATA_TYPE_SENSOR, stackIdOrIndex));

    API.sensors.update(orgId, personId, sensorId, sensorData).then(
      (data) => {
        // Tell the detail view about our great success
        dispatch(dataUpdateSuccess({ orgId, sensorData }, data, DATA_TYPE_SENSOR, stackIdOrIndex));
        // Re-fetch the sensor for the detail view
        dispatch(fetchSensorDetailMixin(orgId, personId, sensorId, stackIdOrIndex));
        // Re-fetch sensors if pages are displaying them
        dispatch(Page.fetchSensors(orgId));
      },
      (error) => {
        dispatch(dataUpdateFailure(
          { orgId, sensorData },
          normalizeError(error, { prependMessage: 'This sensor could not be updated:' }),
          DATA_TYPE_SENSOR,
          stackIdOrIndex
        ));
      }
    );
  };
};

export const createSensorModule = (orgId, sensorId, sensorModuleData, stackIdOrIndex, parentStackId) => {
  const personId = auth.person_id;

  return (dispatch) => {
    dispatch(dataUpdateStart(DATA_TYPE_SENSOR_MODULE, stackIdOrIndex));

    API.sensors.createModule(orgId, personId, sensorId, sensorModuleData).then(
      (data) => {
        // Tell the detail view about our great success
        dispatch(dataUpdateSuccess(
          { orgId, sensorId, sensorModuleData },
          data,
          DATA_TYPE_SENSOR_MODULE,
          stackIdOrIndex
        ));
        // Re-fetch the sensor for the detail view if we know what stack ID that was
        if (parentStackId) {
          dispatch(fetchSensorDetailMixin(orgId, personId, sensorId, parentStackId));
        }
        // Re-fetch sensors if pages are displaying them
        dispatch(Page.fetchSensors(orgId));
        // We should make it
        dispatch(internalDismiss(stackIdOrIndex));
      },
      (error) => {
        dispatch(dataUpdateFailure(
          { orgId, sensorId, sensorModuleData },
          normalizeError(error, { prependMessage: 'This module could not be created:' }),
          DATA_TYPE_SENSOR_MODULE,
          stackIdOrIndex
        ));
      }
    );
  };
};

export const updateSensorModule = (orgId, sensorId, sensorModuleUUID, sensorModuleData, stackIdOrIndex, parentStackId) => {
  const personId = auth.person_id;

  return (dispatch) => {
    dispatch(dataUpdateStart(DATA_TYPE_SENSOR_MODULE, stackIdOrIndex));

    API.sensors.updateModule(orgId, personId, sensorId, sensorModuleUUID, sensorModuleData).then(
      (data) => {
        // Tell the detail view about our great success
        dispatch(dataUpdateSuccess(
          {
            orgId,
            sensorId,
            sensorModuleUUID,
            sensorModuleData,
          },
          data,
          DATA_TYPE_SENSOR_MODULE,
          stackIdOrIndex
        ));
        // Re-fetch the sensor for the detail view if we know what stack ID that was
        if (parentStackId) {
          dispatch(fetchSensorDetailMixin(orgId, personId, sensorId, parentStackId));
        }
        // Re-fetch sensors if pages are displaying them
        dispatch(Page.fetchSensors(orgId));
      },
      (error) => {
        dispatch(dataUpdateFailure(
          {
            orgId,
            sensorId,
            sensorModuleUUID,
            sensorModuleData,
          },
          normalizeError(error, { prependMessage: 'This Module could not be updated:' }),
          DATA_TYPE_SENSOR_MODULE,
          stackIdOrIndex
        ));
      }
    );
  };
};

export const deleteSensorModule = (orgId, sensorId, sensorModuleUUID, stackIdOrIndex, parentStackId) => {
  const personId = auth.person_id;

  return (dispatch) => {
    dispatch(dataUpdateStart(DATA_TYPE_SENSOR_MODULE, stackIdOrIndex));

    API.sensors.deleteModule(orgId, personId, sensorId, sensorModuleUUID).then(
      (data) => {
        // Tell the detail view about our great success
        dispatch(dataUpdateSuccess(
          { orgId, sensorId, sensorModuleUUID },
          data,
          DATA_TYPE_SENSOR_MODULE,
          stackIdOrIndex
        ));
        // Re-fetch the sensor for the detail view if we know what stack ID that was
        if (parentStackId) {
          dispatch(fetchSensorDetailMixin(orgId, personId, sensorId, parentStackId));
        }
        // Re-fetch sensors if pages are displaying them
        dispatch(Page.fetchSensors(orgId));

        // Close flyout
        dispatch(internalDismiss(stackIdOrIndex));
      },
      (error) => {
        dispatch(dataUpdateFailure(
          { orgId, sensorId, sensorModuleUUID },
          normalizeError(error, { prependMessage: 'This module could not be deleted:' }),
          DATA_TYPE_SENSOR_MODULE,
          stackIdOrIndex
        ));
      }
    );
  };
};



// WEBPACK FOOTER //
// ./src/redux/actions/Detail.js